/**
 * SESSÃO ASSINADA NO NAVEGADOR — o par do server/sessao.ts.
 *
 *  - guarda a sessão que o login devolve (`sessao` na resposta de /api/login)
 *    ou que chega pela URL vinda do Portal/outro sistema (`#sso=...&sess=...`);
 *  - anexa `x-sercamp-sessao` em toda chamada `/api/` deste mesmo endereço —
 *    por isso nenhuma tela precisou mudar;
 *  - se o servidor recusar a sessão (401 com `x-sercamp-sessao: invalida`),
 *    apaga o login salvo neste navegador e recarrega: a tela volta ao login.
 *
 * A carga da sessão (login + validade) é lida aqui só para decidir o que fazer;
 * quem confere a assinatura é sempre o servidor.
 *
 * Este arquivo é IGUAL em todos os sistemas e é importado PRIMEIRO no main.tsx.
 */
const CHAVE = "sercamp-sessao";
/** Evita recarregar em looping se o login salvo não for encontrado para apagar. */
const CHAVE_SAIDA = "sercamp-sessao-saiu-em";

function carga(sessao: string): { u?: string; exp?: number } | null {
  try {
    const b64 = sessao.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

export function guardarSessao(sessao: string) {
  try {
    localStorage.setItem(CHAVE, sessao);
  } catch {
    /* navegador sem armazenamento: segue sem sessão */
  }
}

export function encerrarSessao() {
  try {
    localStorage.removeItem(CHAVE);
  } catch {
    /* idem */
  }
}

/** A sessão guardada, se ainda estiver no prazo. */
export function sessaoAtual(): string | null {
  let sessao: string | null = null;
  try {
    sessao = localStorage.getItem(CHAVE);
  } catch {
    return null;
  }
  if (!sessao) return null;
  const exp = carga(sessao)?.exp;
  if (typeof exp !== "number" || exp < Date.now()) {
    encerrarSessao();
    return null;
  }
  return sessao;
}

/** Login dono da sessão guardada (sem conferir assinatura — isso é do servidor). */
export function usuarioDaSessao(): string | null {
  const sessao = sessaoAtual();
  return sessao ? carga(sessao)?.u ?? null : null;
}

/** "&sess=..." para levar a sessão a outro sistema junto do `#sso=` (vazio sem sessão). */
export function trechoDaSessao(): string {
  const sessao = sessaoAtual();
  return sessao ? `&sess=${encodeURIComponent(sessao)}` : "";
}

/** URL de download aberta pelo navegador (sem cabeçalho) levando a sessão. */
export function urlComSessao(url: string): string {
  const sessao = sessaoAtual();
  if (!sessao) return url;
  return url + (url.includes("?") ? "&" : "?") + "sessao=" + encodeURIComponent(sessao);
}

/**
 * Entrada pelo Portal/outro sistema: guarda o `sess=` e tira ele da barra de
 * endereço na hora (o `sso=` e o `tab=` ficam para a tela ler como sempre).
 * Se veio só o `sso=` de outra pessoa, a sessão guardada não é dela: sai.
 */
function lerDaUrl() {
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return;
  const partes = hash.split("&");
  const sess = partes.find((p) => p.startsWith("sess="));
  const sso = partes.find((p) => p.startsWith("sso="));
  if (sess) {
    guardarSessao(decodeURIComponent(sess.slice(5)));
    const resto = partes.filter((p) => p !== sess).join("&");
    history.replaceState(null, "", window.location.pathname + window.location.search + (resto ? `#${resto}` : ""));
  } else if (sso) {
    const quem = decodeURIComponent(sso.slice(4)).toLowerCase();
    if ((usuarioDaSessao() ?? "").toLowerCase() !== quem) encerrarSessao();
  }
}

/**
 * Sessão recusada: apaga a sessão e o login salvo (texto ou JSON com `usuario`)
 * e recarrega. Tela que guarda o login de outro jeito (o Comercial usa várias
 * chaves) escuta o evento `sercamp:sessao-recusada` e limpa o que é dela.
 */
function sairDeTudo(quem: string) {
  window.dispatchEvent(new Event("sercamp:sessao-recusada"));
  encerrarSessao();
  try {
    for (const chave of Object.keys(localStorage)) {
      const valor = localStorage.getItem(chave) ?? "";
      let dono = valor;
      if (valor.startsWith("{")) {
        try {
          dono = String((JSON.parse(valor) as { usuario?: unknown }).usuario ?? "");
        } catch {
          /* não é JSON de login */
        }
      }
      if (quem && dono.trim().toLowerCase() === quem) localStorage.removeItem(chave);
    }
    const ultima = Number(sessionStorage.getItem(CHAVE_SAIDA) ?? 0);
    if (Date.now() - ultima < 15_000) return;
    sessionStorage.setItem(CHAVE_SAIDA, String(Date.now()));
  } catch {
    return;
  }
  window.location.reload();
}

const fetchOriginal = window.fetch.bind(window);

async function fetchComSessao(entrada: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const pedido = entrada instanceof Request ? entrada : null;
  const url = new URL(pedido ? pedido.url : String(entrada), window.location.href);
  if (url.origin !== window.location.origin || !url.pathname.startsWith("/api/")) {
    return fetchOriginal(entrada, init);
  }

  const headers = new Headers(init?.headers ?? pedido?.headers);
  const quem = (headers.get("x-sercamp-usuario") ?? "").trim().toLowerCase();
  const sessao = sessaoAtual();
  // Só manda a sessão se ela for de quem a tela diz que está logado.
  if (sessao && (!quem || quem === (carga(sessao)?.u ?? "").toLowerCase())) {
    headers.set("x-sercamp-sessao", sessao);
  }

  const res = await fetchOriginal(entrada, { ...init, headers });

  if (res.status === 401 && res.headers.get("x-sercamp-sessao") === "invalida") {
    sairDeTudo(quem || (carga(sessao ?? "")?.u ?? "").toLowerCase());
  } else if (res.ok && url.pathname === "/api/login") {
    // Guardada ANTES de devolver a resposta: a tela já faz chamadas logo depois.
    const corpo = (await res.clone().json().catch(() => null)) as { sessao?: unknown } | null;
    if (typeof corpo?.sessao === "string") guardarSessao(corpo.sessao);
  }
  return res;
}

window.fetch = Object.assign(fetchComSessao, fetchOriginal) as typeof fetch;
lerDaUrl();
